import { useState } from "react";
import { AlertTriangle, PackageCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { act } from "@/lib/hooks";
import { api } from "@/lib/api";

const list = (codes) => (codes.length ? codes.join(", ") : "none");

export function StandardMaterials({ onDone }) {
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const open = async () => { const r = await act(() => api.get("/estimating/standard-materials")); if (r) setPlan(r); };
  const apply = async () => {
    setBusy(true);
    const r = await act(() => api.post("/estimating/standard-materials"), "Standard materials loaded");
    setBusy(false);
    if (r) { setPlan(null); onDone(); }
  };
  return (
    <>
      <Button variant="outline" data-testid="load-standard-materials-button" onClick={open}><PackageCheck className="mr-1 h-4 w-4" />Load standard materials</Button>
      {plan && <Dialog open onOpenChange={() => setPlan(null)}>
        <DialogContent data-testid="standard-materials-dialog">
          <DialogHeader><DialogTitle>Load the PressOps material standard?</DialogTitle>
            <DialogDescription>Adds the standard papers, boards and rolls to Estimating materials and Inventory at 0 stock{plan.currency === "GHS" ? " with standard GHS costs" : `, with cost 0 in ${plan.currency} for you to fill in`}.</DialogDescription></DialogHeader>
          <div className="space-y-3 text-sm" data-testid="standard-materials-summary">
            <div className="flex gap-2 rounded-lg border px-3 py-2.5 po-tone-warning" data-testid="standard-materials-warning"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>This replaces the sample papers and rolls. They are removed from Estimating, so new quotes can no longer use them. Existing quotes keep their saved figures. This can't be undone automatically.</span></div>
            <ul className="space-y-1 text-slate-600">
              <li data-testid="std-add-count">Adds <b>{plan.add_materials}</b> estimating materials and <b>{plan.add_items}</b> stock items (all quantities 0)</li>
              {plan.replaced.length > 0 && <li data-testid="std-replaced">Replaced with the standard (not yet used, so standard cost applies): {list(plan.replaced)}</li>}
              {plan.kept_cost.length > 0 && <li data-testid="std-kept-cost">Matched, but your cost is kept because the material is already used on quotes, jobs or stock: {list(plan.kept_cost)}</li>}
              <li data-testid="std-remove-materials">Removes sample materials: {list(plan.remove_materials)}</li>
              <li data-testid="std-remove-items">Removes unused sample stock items: {list(plan.remove_items)}</li>
              {plan.kept_items.length > 0 && <li data-testid="std-kept-items">Kept in Inventory because they have stock or history: {list(plan.kept_items)}</li>}
              <li>Inks, plates, coatings, packaging, mugs, garments and 3D items are kept.</li>
            </ul>
          </div>
          <DialogFooter><Button variant="ghost" onClick={() => setPlan(null)} data-testid="standard-materials-cancel">Cancel</Button>
            <Button data-testid="standard-materials-apply" disabled={busy} onClick={apply}>{busy ? "Loading…" : "Replace and load standard"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>}
    </>
  );
}
